import { useEffect, useState, useMemo } from 'react';

import { OktaAuth, AuthState, AccessToken, IDToken } from '@okta/okta-auth-js';
import { getIsCypressOnLocalhost } from '@sureapp/platform-sdk';

import type { TSaveIdToken, TSaveAccessToken } from '@@/common/interfaces/okta';
import { OktaTokenManager } from '@@/common/services/OktaTokenManager';

type TUseVerifyAuth = {
  oktaAuth: OktaAuth;
  authState: AuthState | null;
  onAuthError: () => void;
  oktaAccessToken: AccessToken | null;
  oktaIdToken: IDToken | null;
  refreshOktaTokens: (oktaAuth: OktaAuth) => Promise<void>;
  saveOktaIdToken: TSaveIdToken;
  saveOktaAccessToken: TSaveAccessToken;
};

export const useVerifyAuth = ({
  oktaAuth,
  authState,
  onAuthError,
  oktaAccessToken,
  oktaIdToken,
  refreshOktaTokens,
  saveOktaAccessToken,
  saveOktaIdToken,
}: TUseVerifyAuth) => {
  const hasTokensInAppState = getIsCypressOnLocalhost()
    ? window.Cypress?.haveTokensLoaded
    : !!(oktaAccessToken && oktaIdToken);

  const [haveTokensLoaded, setHaveTokensLoaded] = useState(
    getIsCypressOnLocalhost() && window.Cypress?.haveTokensLoaded
  );
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  const oktaTokenManager = useMemo(
    () =>
      new OktaTokenManager({
        oktaAuth,
        saveAccessTokenInAppState: saveOktaAccessToken,
        saveIdTokenInAppState: saveOktaIdToken,
      }),
    [oktaAuth, saveOktaAccessToken, saveOktaIdToken]
  );

  useEffect(
    function checkAuthenticationAndGetTokens() {
      if (hasTokensInAppState || isCheckingAuth) {
        return;
      }

      setIsCheckingAuth(true);

      function onTokensLoaded() {
        setHaveTokensLoaded(true);
        setIsCheckingAuth(false);
      }

      async function run() {
        const { isAuthenticated, idToken, accessToken } = authState ?? {};

        if (isAuthenticated && idToken && accessToken) {
          await oktaTokenManager.save({
            accessToken,
            idToken,
          });
          onTokensLoaded();
        } else {
          try {
            await oktaTokenManager.load();
            onTokensLoaded();
          } catch (err) {
            onAuthError();
          }
        }
      }

      run();
    },
    [
      oktaTokenManager,
      oktaAccessToken,
      oktaIdToken,
      onAuthError,
      saveOktaAccessToken,
      saveOktaIdToken,
      hasTokensInAppState,
      authState,
      isCheckingAuth,
    ]
  );

  useEffect(
    function refreshOktaTokensOnLoad() {
      if (hasTokensInAppState && !getIsCypressOnLocalhost()) {
        refreshOktaTokens(oktaAuth).then(() => setHaveTokensLoaded(true));
      }
    },
    [hasTokensInAppState, oktaAuth, refreshOktaTokens]
  );

  return {
    haveTokensLoaded,
  };
};
